import { Box, Flex, Icon, IconButton, Tag, Text } from "@chakra-ui/react";
import { t } from "i18next";
import { useCallback, useRef, useState } from "react";
import { FaArrowRight } from "react-icons/fa";
import { ProjectDto, useCreateUploadForProjectDescriptionMutation } from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { getFileExtension } from "../../../common/documents/utils";
import { useEditor } from "../../../common/editing/save-coordinator";
import { SingleSelector } from "../../../common/input/Selector/SingleSelector";
import { TipTap, type TipTapCreatePostRequest, type TipTapDocumentRemoveHandler } from "../../../common/input/TipTap/TipTap";
import { PersonSelector } from "../../../common/persons/PersonSelector";
import { displayPersonNameWithEmail } from "../../contracts/view-single/sharing/AddExternalParticipantModal";
import {
  organizationEditProjectRoles,
  organizationEditSourcingRoles,
} from "../../organizations/current/manage-users/utils";
import { ProfileImageRounded } from "../../sourcing-events/buyer/view-all/ProfileImageRounded";
import { ProjectSelector } from "../ProjectSelector";
import { useProjectNavigation } from "../useProjectNavigation";
import { useProjectTypes } from "../useProjectTypes";

export const ProjectDetails = ({ projectId }: { projectId: string }) => {
  const { state: project, updateField, entityStatus } = useEditor<ProjectDto>(projectId);
  const [createUploadForDescription] = useCreateUploadForProjectDescriptionMutation();
  const projectTypes = useProjectTypes();
  const goToProject = useProjectNavigation();
  const authState = useLoggedInWithOrgContextState();

  const [editProjectResponsible, setEditProjectResponsible] = useState(false);
  const [editSourcingResponsible, setEditSourcingResponsible] = useState(false);
  const [editProjectType, setEditProjectType] = useState(false);
  const [editParentProject, setEditParentProject] = useState(false);

  const descriptionDocumentsRef = useRef(project.descriptionDocuments);
  descriptionDocumentsRef.current = project.descriptionDocuments;

  const createPostRequest = useCallback<TipTapCreatePostRequest>(
    async ({ uploadId, file }) =>
      createUploadForDescription({
        projectId,
        uploadId,
        createUploadRequestDto: {
          fileName: file.name,
          fileSize: file.size,
          fileExtension: getFileExtension(file.name),
          mimeType: file.type,
        },
      }).unwrap(),
    [createUploadForDescription, projectId]
  );

  const handleRemoveDocument = useCallback<TipTapDocumentRemoveHandler>(
    async ({ documentId }) => {
      updateField({
        descriptionDocuments: (descriptionDocumentsRef.current ?? []).filter((d) => d.id !== documentId),
      });
    },
    [updateField]
  );

  return (
    <>
      <Text fontWeight="bold" pb="1">
        {t("Description")}
      </Text>
      <TipTap
        content={project.description ?? ""}
        editable={project.canEdit}
        onUpdate={(value) => updateField({ description: value })}
        onDocumentRemove={handleRemoveDocument}
        createPostRequest={createPostRequest}
        documents={project.descriptionDocuments ?? undefined}
      />
      {editProjectResponsible ? (
        <Box maxWidth={{ md: "60%" }} py="3">
          <Text fontWeight="bold" pb="1">
            {t("Project responsible")}
          </Text>
          <PersonSelector
            organizationIds={[authState.selectedOrg.id]}
            selectedPerson={project.projectResponsible}
            roles={organizationEditProjectRoles}
            onChange={(person) => {
              updateField({ projectResponsible: person });
              setEditProjectResponsible(false);
            }}
            onBlur={() => setEditProjectResponsible(false)}
            autoFocus
          />
        </Box>
      ) : (
        <Box py="4">
          <Text fontWeight="bold" pb="1">
            {t("Project responsible")}
          </Text>
          <Flex
            alignItems="center"
            cursor={project.canEdit ? "pointer" : "auto"}
            onClick={() => {
              if (project.canEdit) setEditProjectResponsible(true);
            }}
          >
            <ProfileImageRounded
              name={project.projectResponsible?.firstName ?? ""}
              width="5"
              backgroundColor="smSecondary"
              fontSize="xx-small"
            />
            {project.projectResponsible ? (
              <Text fontSize="md" pl="2">
                {displayPersonNameWithEmail(project.projectResponsible)}
              </Text>
            ) : (
              <Text fontStyle="italic" fontSize="md" color="smMuted" pl="2">
                {t("Project responsible not provided")}
              </Text>
            )}
          </Flex>
        </Box>
      )}
      {editSourcingResponsible ? (
        <Box maxWidth={{ md: "60%" }} pb="3">
          <Text fontWeight="bold" pb="1">
            {t("Sourcing responsible")}
          </Text>
          <PersonSelector
            organizationIds={[authState.selectedOrg.id]}
            selectedPerson={project.sourcingResponsible}
            roles={organizationEditSourcingRoles}
            onChange={(person) => {
              updateField({ sourcingResponsible: person });
              setEditSourcingResponsible(false);
            }}
            onBlur={() => setEditSourcingResponsible(false)}
            autoFocus
          />
        </Box>
      ) : (
        <Box pb="4">
          <Text fontWeight="bold" pb="1">
            {t("Sourcing responsible")}
          </Text>
          <Flex
            alignItems="center"
            cursor={project.canEdit ? "pointer" : "auto"}
            onClick={() => {
              if (project.canEdit) setEditSourcingResponsible(true);
            }}
          >
            <ProfileImageRounded
              name={project.sourcingResponsible?.firstName ?? ""}
              width="5"
              backgroundColor="smTertiary"
              fontSize="xx-small"
            />
            {project.sourcingResponsible ? (
              <Text fontSize="md" pl="2">
                {displayPersonNameWithEmail(project.sourcingResponsible)}
              </Text>
            ) : (
              <Text fontStyle="italic" color="smMuted" pl="2">
                {t("Sourcing responsible not provided")}
              </Text>
            )}
          </Flex>
        </Box>
      )}
      {editProjectType ? (
        <Box maxWidth={{ md: "40%" }} pb="3">
          <Text fontWeight="bold" pb="1">
            {t("Project type")}
          </Text>
          <SingleSelector
            size="sm"
            isLoading={entityStatus === "saving"}
            value={projectTypes.find((p) => p.value === project.projectType?.id)}
            options={projectTypes}
            onChange={(o) => {
              const projectTypeOption = projectTypes.find((p) => p.value === o?.value);
              if (!projectTypeOption) throw Error("Selected project type not found");
              updateField({ projectType: { id: projectTypeOption.value, name: projectTypeOption.name } });
              setEditProjectType(false);
            }}
            onBlur={() => setEditProjectType(false)}
            autoFocus
          />
        </Box>
      ) : (
        <Box pb="4">
          <Text fontWeight="bold" pb="1">
            {t("Project type")}
          </Text>
          <Tag
            cursor={project.canEdit ? "pointer" : "auto"}
            onClick={() => {
              if (project.canEdit) setEditProjectType(true);
            }}
          >
            {project.projectType?.name ? t(project.projectType.name) : t("Project type not provided")}
          </Tag>
        </Box>
      )}
      <Text fontWeight="bold" pb="1">
        {t("Parent project")}
      </Text>
      {editParentProject ? (
        <Flex maxWidth="60%" pb="3">
          <ProjectSelector
            selectedProject={project.parent}
            projectsToExclude={[projectId]}
            onChange={(p) => {
              updateField({ parent: p });
              setEditParentProject(false);
            }}
            onBlur={() => setEditParentProject(false)}
            autoFocus={true}
          />
        </Flex>
      ) : (
        <Box cursor="pointer" pb="4">
          {project.parent ? (
            <Flex>
              <Tag
                cursor={project.canEdit ? "pointer" : "auto"}
                onClick={() => {
                  if (project.canEdit) setEditParentProject(true);
                }}
              >
                {`${project.parent.externalId ? `${project.parent.externalId} - ` : ""}${project.parent.name}`}
              </Tag>
              <IconButton
                aria-label="go to project"
                icon={<Icon as={FaArrowRight} />}
                variant={"link"}
                colorScheme="cyan"
                onClick={() => goToProject(project.parent?.id ?? "")}
              />
            </Flex>
          ) : (
            <Text
              fontStyle="italic"
              color="smMuted"
              onClick={() => {
                if (project.canEdit) setEditParentProject(true);
              }}
            >
              {t("Parent project not provided")}
            </Text>
          )}
        </Box>
      )}
      <Text fontWeight="bold" pb="1">
        {t("Child projects")}
      </Text>
      {project.children && (
        <Flex flexDirection="column" rowGap="1" pb="4">
          {project.children.map((c) => (
            <Flex key={c.id}>
              <Tag>{`${c.externalId ? `${c.externalId} - ` : ""}${c.name}`}</Tag>
              <IconButton
                aria-label="go to project"
                icon={<Icon as={FaArrowRight} />}
                variant={"link"}
                colorScheme="cyan"
                onClick={() => goToProject(c?.id ?? "")}
              />
            </Flex>
          ))}
        </Flex>
      )}
      {!project.children?.length && (
        <Text fontStyle="italic" color="smMuted" pb="4">
          {t("Child projects not provided")}
        </Text>
      )}
    </>
  );
};
